import React, { useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import API_BASE_URL from "../../api/config";
import COLLEGE_MAP from "../../constants/colleges";

// --- Icons ---
const SearchIcon = () => (
  <svg
    className="search-icon"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
    />
  </svg>
);

const EmptyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
    />
  </svg>
);

// --- UI Components ---
const LoadingSpinner = ({ message = "Loading Data..." }) => (
  <div className="loading-container">
    <div className="spinner"></div>
    <p>{message}</p>
  </div>
);

const EmptyState = ({ message, itemType }) => (
  <div className="empty-state-container">
    <EmptyIcon />
    <h3>{message}</h3>
    <p>There are no {itemType} matching your criteria at the moment.</p>
  </div>
);

// --- NEW: Admin View Toggle Component ---
const AdminViewToggle = ({ selectedView, onSelectView }) => (
  <div className="admin-toggle-container">
    <button
      className={`toggle-btn ${selectedView === "area_leader" ? "active" : ""}`}
      onClick={() => onSelectView("area_leader")}
    >
      Area Leaders
    </button>
    <button
      className={`toggle-btn ${selectedView === "facilitator" ? "active" : ""}`}
      onClick={() => onSelectView("facilitator")}
    >
      Facilitators
    </button>
  </div>
);

// The configs object remains the same, as the keys are used dynamically
const configs = {
  admin: {
    fetchUrl: `${API_BASE_URL}/api/Facilitator_Panel/read`,
    approveUrl: `${API_BASE_URL}/api/Facilitator_Panel/approve_area_leader`,
    title: "Area Leader Requests",
    itemType: "Area Leaders",
    headerName: "Area Leader Name",
  },
  area_leader: {
    fetchUrl: `${API_BASE_URL}/api/Facilitator_Panel/read`,
    approveUrl: `${API_BASE_URL}/api/Facilitator_Panel/approve_facilitator`,
    title: "Facilitator Requests",
    itemType: "Facilitators",
    headerName: "Facilitator Name",
  },
};

const Requests = () => {
  const [searchText, setSearchText] = useState("");
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [approvingEmail, setApprovingEmail] = useState("");
  const [pageConfig, setPageConfig] = useState(null);
  const [areaOfAreaLeader, setAreaofAreaLeader] = useState(null);
  const [areaEdits, setAreaEdits] = useState({});
  const [updatedRows, setUpdatedRows] = useState({});

  // --- NEW: State to manage which view the admin has selected ---
  const [adminSelectedView, setAdminSelectedView] = useState("area_leader");
  const userRole = Cookies.get("role");

  const fetchData = useCallback(async () => {
    if (!pageConfig) return;
    setIsLoading(true);
    try {
      const res = await fetch(pageConfig.fetchUrl);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();

      const filterByView = (data) => {
        if (adminSelectedView === "area_leader") {
          // show only users who are NOT admin and NOT already area_leader
          return data.filter(
            (item) => item.role !== "admin" && item.role !== "facilitator",
          );
        }

        if (adminSelectedView === "facilitator") {
          // show only users who are NOT admin
          return data.filter(
            (item) => item.role !== "admin" && item.role !== "area_leader",
          );
        }

        return data;
      };

      const normalizedData = Array.isArray(data)
        ? data.map((item) => ({
            name: item.UserName || item.username,
            email: item.Email || item.email,
            approved: item.role_approved,
            role: item.role || null, // 👈 IMPORTANT
            area: item.Area || "",
          }))
        : [];

      const sortedData = normalizedData.sort((a, b) => {
        if (a.approved === "0" && b.approved !== "0") return -1;
        if (a.approved !== "0" && b.approved === "0") return 1;
        return 0;
      });

      const viewFilteredData =
        userRole === "admin" ? filterByView(sortedData) : sortedData;

      setAllData(viewFilteredData);
      setFilteredData(viewFilteredData);
      setUpdatedRows({});
      setAreaEdits({});
    } catch (error) {
      console.error(`Error fetching ${pageConfig.itemType} data:`, error);
      Swal.fire({
        icon: "error",
        title: "Data Fetch Error",
        text: `Could not fetch ${pageConfig.itemType.toLowerCase()} data. Please try again later.`,
      });
      setAllData([]);
      setFilteredData([]);
    } finally {
      setIsLoading(false);
    }
  }, [pageConfig, adminSelectedView, userRole]);

  const authorizeAreaLeader = useCallback(async (email) => {
    setIsLoading(true);
    try {
      const res = await fetch(configs.admin.fetchUrl);
      if (!res.ok) throw new Error("Could not verify approval status.");

      const leaders = await res.json();
      const currentUser = Array.isArray(leaders)
        ? leaders.find((leader) => (leader.Email || leader.email) === email)
        : null;

      if (currentUser && currentUser.role_approved === "1") {
        // Config for Area Leader viewing Facilitators
        setPageConfig(configs.area_leader);
        setAreaofAreaLeader(currentUser.Area);
      } else {
        setIsLoading(false);
        Swal.fire({
          icon: "warning",
          title: "Account Pending Approval",
          text: "Your account has not been approved by an administrator yet. You cannot view reports at this time.",
          confirmButtonText: "Go Back",
        }).then(() => {
          window.history.back();
        });
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Authorization error:", error);
      Swal.fire({
        icon: "error",
        title: "Authorization Failed",
        text: "Could not verify your account status. Please try again later.",
      }).then(() => {
        window.history.back();
      });
    }
  }, []);

  useEffect(() => {
    const userEmail = Cookies.get("email");

    if (!userRole) {
      setIsLoading(false);
      Swal.fire({
        icon: "error",
        title: "Authentication Error",
        text: "Your session has expired. Please log in again.",
      }).then(() => window.history.back());
      return;
    }

    if (userRole === "admin") {
      // Logic moved to the new useEffect hook below that depends on `adminSelectedView`
      // This hook now just confirms the role is valid
      if (adminSelectedView === "area_leader") {
        setPageConfig(configs.admin);
      } else {
        // For 'facilitator' view, we use the config defined under 'area_leader' key
        setPageConfig(configs.area_leader);
      }
    } else if (userRole === "area_leader") {
      if (!userEmail) {
        setIsLoading(false);
        Swal.fire({
          icon: "error",
          title: "Authentication Error",
          text: "Could not find your email in the session. Please log in again.",
        }).then(() => window.history.back());
        return;
      }
      authorizeAreaLeader(userEmail);
    } else {
      setIsLoading(false);
      Swal.fire({
        icon: "error",
        title: "Unauthorized Access",
        text: "You do not have permission to view this page.",
        confirmButtonText: "Go Back",
      }).then(() => {
        window.history.back();
      });
    }
  }, [authorizeAreaLeader, userRole, adminSelectedView]);

  useEffect(() => {
    if (pageConfig) {
      fetchData();
    }
  }, [pageConfig, fetchData]);

  const handleAreaChange = (email, value) => {
    setAreaEdits((prev) => ({
      ...prev,
      [email]: value,
    }));
  };

  const handleApprovalChange = async (email, newStatus) => {
    if (!pageConfig) return;
    setApprovingEmail(email);

    const role =
      adminSelectedView === "facilitator" ? "facilitator" : "area_leader";

    const statusConfig = {
      // For an approval action
      1: {
        action: "Approving",
        title: "Approved!",
        message: "approved",
      },
      // For any other action (the "not approve" case)
      default: {
        action: "Revoking",
        title: "Revoked!",
        message: "revoked",
      },
    };

    const selectedArea =
      areaEdits[email] ?? allData.find((i) => i.email === email)?.area;
    if (!selectedArea || !selectedArea.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Area Required",
        text: "Please select or enter an area before approving.",
      });
      return;
    }

    const config = statusConfig[newStatus] || statusConfig.default;
    const isRevoke = newStatus === "0";
    const actionText = config.action;
    const successTitle = config.title;
    const successText = `${email} has been ${config.message} successfully.`;
    const payload =
      newStatus === "1"
        ? { email, Approved: newStatus, role, area: selectedArea }
        : { email, Approved: newStatus, area: selectedArea };

    try {
      const res = await fetch(pageConfig.approveUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.success) {
        if (isRevoke) {
          setAreaEdits((prev) => {
            const copy = { ...prev };
            delete copy[email];
            return copy;
          });

          setUpdatedRows((prev) => {
            const copy = { ...prev };
            delete copy[email];
            return copy;
          });
        } else {
          setUpdatedRows((prev) => ({
            ...prev,
            [email]: true,
          }));
        }

        Swal.fire({
          icon: "success",
          title: successTitle,
          text: successText,
        });

        fetchData();
      }
    } catch (error) {
      console.error(`${actionText} error:`, error);
      Swal.fire({
        icon: "error",
        title: `${actionText} Failed`,
        text: error.message || "Something went wrong",
      });
    } finally {
      setApprovingEmail("");
    }
  };

  const isAreaUpdated = (item) => updatedRows[item.email];

  useEffect(() => {
    const lowerCaseSearchText = searchText.toLowerCase();
    const newFilteredData = allData.filter(
      (item) =>
        (item.name && item.name.toLowerCase().includes(lowerCaseSearchText)) ||
        (item.email && item.email.toLowerCase().includes(lowerCaseSearchText)),
    );
    setFilteredData(newFilteredData);
  }, [searchText, allData]);

  if (isLoading) {
    return <LoadingSpinner message="Verifying access and loading data..." />;
  }

  if (!pageConfig) {
    return null;
  }

  return (
    <div className="main">
      <div className="data-container">
        <div className="container-header">
          <h1 className="header-title">{pageConfig.title}</h1>
          <div className="search-box">
            <SearchIcon />
            <input
              className="search-input"
              type="text"
              placeholder="Search by name or email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>

        {/* --- NEW: Conditionally render the toggle for admins --- */}
        {userRole === "admin" && (
          <AdminViewToggle
            selectedView={adminSelectedView}
            onSelectView={setAdminSelectedView}
          />
        )}

        <div className="table-wrapper">
          {filteredData.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{pageConfig.headerName}</th>
                  <th>Email</th>
                  <th>Area</th>
                  <th>Approval Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData
                  .filter((item) => {
                    // Admin → no restriction here
                    if (userRole === "admin") return true;
                    // Area Leader → ONLY facilitators of same area
                    if (userRole === "area_leader") {
                      return (
                        item.role === "facilitator" &&
                        item.area === areaOfAreaLeader
                      );
                    }
                    return false;
                  })
                  .map((item) => {
                    return (
                      <tr key={item.email}>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>
                          {userRole === "admin" && (
                            <select
                              className="input-cell"
                              value={areaEdits[item.email] ?? item.area}
                              onChange={(e) =>
                                handleAreaChange(item.email, e.target.value)
                              }
                            >
                              <option value="">Select Area</option>
                              {Object.entries(COLLEGE_MAP).map(
                                ([key, label]) => (
                                  <option key={key} value={key}>
                                    {label}
                                  </option>
                                ),
                              )}
                            </select>
                          )}
                          {userRole === "area_leader" && (
                            <span>{COLLEGE_MAP[item.area] || item.area}</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${isAreaUpdated(item) ? "status-updated" : item.approved === "1" ? "status-approved-request" : "status-not-approved"}`}
                          >
                            {isAreaUpdated(item)
                              ? "Updated"
                              : item.approved === "1"
                                ? "Approved"
                                : "Pending"}
                          </span>

                          {(item.approved === "0" ||
                            item.approved === null) && (
                            <button
                              className="approve-btn"
                              onClick={() =>
                                handleApprovalChange(item.email, "1")
                              }
                              disabled={approvingEmail === item.email}
                            >
                              {approvingEmail === item.email
                                ? "Approving..."
                                : "Approve"}
                            </button>
                          )}

                          {item.approved === "1" && (
                            <button
                              className="remove-btn"
                              onClick={() =>
                                handleApprovalChange(item.email, "0")
                              }
                              disabled={approvingEmail === item.email}
                            >
                              {approvingEmail === item.email
                                ? "Revoking..."
                                : "Revoke"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          ) : (
            <EmptyState
              message={`No ${pageConfig.itemType} Found`}
              itemType={pageConfig.itemType.toLowerCase()}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Requests;
